import React, { useState, useEffect, useMemo } from "react";
import ThemeableAgGrid from "../../../common/components/agGrid/ThemeableAgGrid";
import Loading from "../../../common/components/loading/loading";
import { getAliasHistory } from "../../../common/services/products/aliasService";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheck, faTimes } from "@fortawesome/free-solid-svg-icons";

const AliasHistory = ({ aliasId }) => {
  const { t } = useTranslation(["products"]);
  const [aliasHistory, setAliasHistory] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  // Memoize column definitions
  const columnDefs = useMemo(
    () => [
      {
        field: "id",
        headerName: t("RecordID"),
        sortable: true,
        filter: true,
      },
      {
        field: "name",
        headerName: t("AliasName"),
        sortable: true,
        filter: true,
      },
      {
        field: "productName",
        headerName: t("Product"),
        sortable: true,
        filter: true,
      },
      {
        field: "organizationName",
        headerName: t("Organization"),
        sortable: true,
        filter: true,
      },
      {
        field: "active",
        headerName: t("Active"),
        flex: 1,
        // Apply centered class for better alignment
        cellClass: "ag-center-aligned-cell",
        headerClass: "ag-center-aligned-header",
        filterValueGetter: (params) => params.data.activeString,
        cellRenderer: (params) => {
          const isActive = params.data?.active;
          return (
            <div className="centered" style={{ width: "100%", height: "100%" }}>
              <FontAwesomeIcon
                icon={isActive ? faCheck : faTimes}
                className={isActive ? "text-success" : "text-danger"}
                style={{ fontSize: "16px" }}
              />
            </div>
          );
        },
      },
      {
        field: "updatedBy",
        headerName: t("UpdatedBy"),
        sortable: true,
        filter: true,
      },
      {
        field: "updatedDate",
        headerName: t("UpdateDate"),
        sortable: true,
        filter: true,
      },
      {
        field: "writeDate",
        headerName: t("WriteDate"),
        sortable: true,
        filter: true,
      },
      {
        field: "eventId",
        headerName: t("EventID"),
        sortable: true,
        filter: true,
      },
    ],
    [t]
  );

  const mapToViewModel = (aliases) => {
    const dateFormatOptions = {
      year: "numeric",
      month: "2-digit",
      day: "numeric",
      hour: "numeric",
      hour12: false,
      minute: "numeric",
      second: "numeric",
    };

    return aliases.map((alias) => ({
      id: alias.id,
      name: alias.name,
      productName: alias.productName,
      organizationName: alias.organizationName,
      active: alias.active,
      updatedByUserAccountId: alias.createdByUserAccountId,
      updatedBy:
        alias.updatedByUserAccountFirstName +
        " " +
        alias.updatedByUserAccountLastName,
      updatedDate: new Date(alias.updatedDate).toLocaleDateString(
        "en-US",
        dateFormatOptions
      ),
      writeDate: new Date(alias.writeDate).toLocaleDateString(
        "en-US",
        dateFormatOptions
      ),
      eventId: alias.eventId,
    }));
  };

  // Fetch history data when component mounts or aliasId changes
  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      try {
        const response = await getAliasHistory(aliasId);
        const formattedHistory = mapToViewModel(response);
        setAliasHistory(formattedHistory);
      } catch (error) {
        console.error("Error fetching alias history:", error);
      } finally {
        setIsLoading(false);
      }
    };

    if (aliasId) {
      fetchData();
    }
  }, [aliasId]);

  if (isLoading) return <Loading />;

  return (
    <div className="history-table-container">
      {t("AliasID")} {aliasId}
      <ThemeableAgGrid
        className="table-extra-small"
        columnDefs={columnDefs}
        rowData={aliasHistory}
        defaultColDef={{
          resizable: true,
        }}
        getRowId={(params) => params.data.id}
        animateRows={true}
      />
    </div>
  );
};

export default AliasHistory;

